import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { IUsuario as Usuario } from '../types/firestore.types';

const COLECAO_USUARIOS = 'usuarios';

const converterSnapshotParaUsuario = (
  docSnapshot: QueryDocumentSnapshot<DocumentData, DocumentData>
): Usuario => {
  const dados = docSnapshot.data();
  return {
    id: docSnapshot.id,
    nome: dados.nome,
    email: dados.email,
    tipo_usuario: dados.tipo_usuario,
    unidade_id: dados.unidade_id || null,
    criado_em: dados.criado_em,
    atualizado_em: dados.atualizado_em,
  };
};

export const criarUsuario = async (
  usuarioData: Omit<Usuario, 'id' | 'criado_em' | 'atualizado_em'>
): Promise<Usuario> => {
  try {
    const agora = Timestamp.now();
    const novoUsuarioRef = await addDoc(collection(db, COLECAO_USUARIOS), {
      ...usuarioData,
      criado_em: agora,
      atualizado_em: agora,
    });
    const novoUsuarioDoc = await getDoc(novoUsuarioRef);
    if (!novoUsuarioDoc.exists()) {
      throw new Error('Falha ao recuperar o usuário recém-criado.');
    }
    return converterSnapshotParaUsuario(novoUsuarioDoc);
  } catch (error) {
    console.error('Erro ao criar usuário:', error);
    throw new Error(`Não foi possível criar o usuário: ${(error as Error).message}`);
  }
};

export const listarUsuarios = async (): Promise<Usuario[]> => {
  try {
    const q = query(collection(db, COLECAO_USUARIOS), orderBy('nome', 'asc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(converterSnapshotParaUsuario);
  } catch (error) {
    console.error('Erro ao listar usuários:', error);
    throw new Error(`Não foi possível listar os usuários: ${(error as Error).message}`);
  }
};

export const buscarUsuarioPorId = async (id: string): Promise<Usuario | null> => {
  try {
    const usuarioRef = doc(db, COLECAO_USUARIOS, id);
    const usuarioDoc = await getDoc(usuarioRef);
    if (usuarioDoc.exists()) {
      return converterSnapshotParaUsuario(usuarioDoc);
    }
    return null;
  } catch (error) {
    console.error(`Erro ao buscar usuário com ID ${id}:`, error);
    throw new Error(`Não foi possível buscar o usuário: ${(error as Error).message}`);
  }
};

export const buscarUsuariosPorTipo = async (tipoUsuario: string): Promise<Usuario[]> => {
  try {
    const q = query(
      collection(db, COLECAO_USUARIOS),
      where('tipo_usuario', '==', tipoUsuario),
      orderBy('nome', 'asc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(converterSnapshotParaUsuario);
  } catch (error) {
    console.error(`Erro ao buscar usuários do tipo ${tipoUsuario}:`, error);
    throw new Error(`Não foi possível buscar usuários por tipo: ${(error as Error).message}`);
  }
};

export const atualizarUsuario = async (
  id: string,
  dadosAtualizados: Partial<Omit<Usuario, 'id' | 'criado_em' | 'atualizado_em'>>
): Promise<void> => {
  try {
    const usuarioRef = doc(db, COLECAO_USUARIOS, id);
    await updateDoc(usuarioRef, {
      ...dadosAtualizados,
      atualizado_em: Timestamp.now(),
    });
  } catch (error) {
    console.error(`Erro ao atualizar usuário com ID ${id}:`, error);
    throw new Error(`Não foi possível atualizar o usuário: ${(error as Error).message}`);
  }
};

export const excluirUsuario = async (id: string): Promise<void> => {
  try {
    const usuarioRef = doc(db, COLECAO_USUARIOS, id);
    await deleteDoc(usuarioRef);
  } catch (error) {
    console.error(`Erro ao excluir usuário com ID ${id}:`, error);
    throw new Error(`Não foi possível excluir o usuário: ${(error as Error).message}`);
  }
};